/**
 * loaderPreProcessJson_Api.ts - API support helpers for LoaderPreProcessJson.
 * Holds the private serialization helpers used by getTemplatesApiResponse.
 */

import { JsonUtility } from '../../../../Assembler/dist/app/jsonUtility.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { AppSiteTemplateData } from '../../../../Assembler/dist/model/appSiteTemplateData.js';
import { LoaderJsonHelper } from '../../base/loader/loaderJsonHelper.js';
import type { TargetFile } from '../../model/targetFile.js';
import type { AppSiteMetadata, JsonKeyEntryInfo, TemplateEntryInfo, TemplateInfo } from '../../model/appSiteMetadata.js';
import type { IConfig } from '../../../../Assembler/dist/config/iConfig.js';
import type { LoaderPreProcessJson } from './loaderPreProcessJson.js';

/**
 * Gets all templates as dictionary of AppSite to template data.
 */
export function getAllTemplatesJson(
    loader: LoaderPreProcessJson,
    htmlJsonCache: Map<string, Map<string, TargetFile>>,
    dataJsonCache: Map<string, Map<string, TargetFile>>,
    metadataCache: Map<string, AppSiteMetadata>,
    primaryAppSite: string,
    _config: IConfig,
    includeSearchAppSites: boolean = true,
    maskHtml: boolean = false,
    orderedTemplateNames: string[] | null = null
): Record<string, AppSiteTemplateData> {
    const result: Record<string, AppSiteTemplateData> = {};
    const searchAppSites = includeSearchAppSites ? loader.getSearchAppSitesForAppSite(primaryAppSite) : '';
    const appSitesToInclude = (loader.constructor as typeof LoaderPreProcessJson).buildAppSitesToLoad(primaryAppSite, searchAppSites);

    for (const appSite of appSitesToInclude) {
        if (!htmlJsonCache.has(appSite) && !dataJsonCache.has(appSite)) continue;
        const templatesJson = buildTemplatesJsonForAppSite(htmlJsonCache, dataJsonCache, appSite, maskHtml, orderedTemplateNames);
        const metadataJson = buildMetadataJsonForAppSite(metadataCache, appSite);
        result[appSite] = new AppSiteTemplateData(appSite, templatesJson, metadataJson, _config.getAppFile(appSite, '').appFile);
    }

    return result;
}

/**
 * Builds templates JSON for a single AppSite.
 */
export function buildTemplatesJsonForAppSite(
    htmlJsonCache: Map<string, Map<string, TargetFile>>,
    dataJsonCache: Map<string, Map<string, TargetFile>>,
    appSite: string,
    maskHtml: boolean = false,
    orderedTemplateNames: string[] | null = null
): string {
    const templateData: Record<string, { HtmlJson: unknown; DataJson: unknown }> = {};

    const htmlTemplates = htmlJsonCache.get(appSite);
    if (htmlTemplates) {
        for (const [templateName, htmlFile] of htmlTemplates.entries()) {
            const templateKey = `${appSite}_${templateName}`;
            templateData[templateKey] = {
                HtmlJson: htmlFile.jsonStructure,
                DataJson: null
            };
        }
    }

    const dataTemplates = dataJsonCache.get(appSite);
    if (dataTemplates) {
        for (const [templateKey, dataFile] of dataTemplates.entries()) {
            const lowerKey = templateKey.toLowerCase();
            const existingKey = Object.keys(templateData).find(k => k.toLowerCase() === lowerKey);
            if (existingKey) {
                templateData[existingKey].DataJson = dataFile.jsonStructure;
            } else {
                templateData[templateKey] = {
                    HtmlJson: null,
                    DataJson: dataFile.jsonStructure
                };
            }
        }
    }

    let templateKeys: string[];
    if (orderedTemplateNames && orderedTemplateNames.length > 0) {
        templateKeys = [];
        const addedKeys = new Set<string>();
        const allKeys = Object.keys(templateData);

        for (const name of orderedTemplateNames) {
            const componentName = name.startsWith('!') ? name.slice(1) : name;
            const componentLower = componentName.toLowerCase();
            for (const key of allKeys) {
                if (addedKeys.has(key)) continue;
                const keyLower = key.toLowerCase();
                if (keyLower === componentLower || keyLower.endsWith(`_${componentLower}`)) {
                    templateKeys.push(key);
                    addedKeys.add(key);
                    break;
                }
            }
        }
    } else {
        templateKeys = Object.keys(templateData);
    }

    const sb: string[] = [];
    sb.push('{');

    let first = true;
    for (const key of templateKeys) {
        const data = templateData[key];
        if (!data) continue;
        if (!first) sb.push(',');
        first = false;

        sb.push('"');
        sb.push(JsonUtility.escapeJson(key));
        sb.push('":{"HtmlJson":');

        if (data.HtmlJson) {
            // Emit HtmlJson as an OPAQUE byte artifact via the single shared encoder, so
            // ConfigClient and ConfigOCI receive byte-identical artifacts (format owned by loader).
            LoaderJsonHelper.appendEncodedArtifact(sb, data.HtmlJson as JsonObject, maskHtml);
        } else {
            sb.push('null');
        }

        sb.push(',"DataJson":');
        if (data.DataJson) {
            sb.push(JsonUtility.serializeObject(data.DataJson));
        } else {
            sb.push('null');
        }
        sb.push('}');
    }

    sb.push('}');
    return sb.join('');
}

/**
 * Builds metadata JSON for a single AppSite.
 */
export function buildMetadataJsonForAppSite(
    metadataCache: Map<string, AppSiteMetadata>,
    appSite: string
): string {
    const parts: string[] = [];
    parts.push('{');

    const appSiteLower = appSite.toLowerCase();
    const keys = Array.from(metadataCache.keys());
    let first = true;
    for (const metadataKey of keys) {
        if (!metadataKey.toLowerCase().startsWith(appSiteLower)) {
            continue;
        }

        if (!first) parts.push(',');
        first = false;

        parts.push('"');
        parts.push(JsonUtility.escapeJson(metadataKey));
        parts.push('":');
        parts.push(serializeMetadataToJson(metadataCache.get(metadataKey)!));
    }

    parts.push('}');
    return parts.join('');
}

/**
 * Serializes AppSiteMetadata to JSON.
 */
export function serializeMetadataToJson(metadata: AppSiteMetadata): string {
    const parts: string[] = [];
    parts.push('{');
    parts.push('"appSiteName":"');
    parts.push(JsonUtility.escapeJson(metadata.appSiteName || ''));
    parts.push('","appFile":"');
    parts.push(JsonUtility.escapeJson(metadata.appFile || ''));
    parts.push('","appView":"');
    parts.push(JsonUtility.escapeJson(metadata.appView || ''));
    parts.push('","searchAppSites":"');
    parts.push(JsonUtility.escapeJson(metadata.searchAppSites || ''));
    parts.push('"');

    parts.push(',"appViews":[');
    if (metadata.appViews && metadata.appViews.length > 0) {
        for (let i = 0; i < metadata.appViews.length; i++) {
            if (i > 0) parts.push(',');
            parts.push('"');
            parts.push(JsonUtility.escapeJson(metadata.appViews[i]));
            parts.push('"');
        }
    }
    parts.push(']');

    if (metadata.rootComponent) {
        parts.push(',"rootComponent":');
        parts.push(serializeTemplateInfoToJson(metadata.rootComponent));
    }

    parts.push('}');
    return parts.join('');
}

/**
 * Serializes TemplateInfo recursively to JSON.
 */
export function serializeTemplateInfoToJson(template: TemplateInfo): string {
    const parts: string[] = [];
    parts.push('{');
    parts.push('"templateName":"');
    parts.push(JsonUtility.escapeJson(template.templateName || ''));
    parts.push('","templateAppSite":"');
    parts.push(JsonUtility.escapeJson(template.templateAppSite || ''));
    parts.push('","templateType":"');
    parts.push(JsonUtility.escapeJson(template.templateType || ''));
    parts.push('","placeholderName":"');
    parts.push(JsonUtility.escapeJson(template.placeholderName || ''));
    parts.push('"');

    parts.push(',"jsonKeys":[');
    if (template.jsonKeys && template.jsonKeys.length > 0) {
        for (let i = 0; i < template.jsonKeys.length; i++) {
            if (i > 0) parts.push(',');
            parts.push('"');
            parts.push(JsonUtility.escapeJson(template.jsonKeys[i]));
            parts.push('"');
        }
    }
    parts.push(']');

    parts.push(',"jsonArrayKeys":[');
    if (template.jsonArrayKeys && template.jsonArrayKeys.length > 0) {
        for (let i = 0; i < template.jsonArrayKeys.length; i++) {
            if (i > 0) parts.push(',');
            parts.push('"');
            parts.push(JsonUtility.escapeJson(template.jsonArrayKeys[i]));
            parts.push('"');
        }
    }
    parts.push(']');

    parts.push(',"jsonInheritKeys":{');
    if (template.jsonInheritKeys && template.jsonInheritKeys.size > 0) {
        let first = true;
        for (const [key, value] of template.jsonInheritKeys) {
            if (!first) parts.push(',');
            first = false;
            parts.push('"');
            parts.push(JsonUtility.escapeJson(key));
            parts.push('":"');
            parts.push(JsonUtility.escapeJson(value));
            parts.push('"');
        }
    }
    parts.push('}');

    parts.push(',"placeholders":[');
    if (template.placeholders && template.placeholders.length > 0) {
        for (let i = 0; i < template.placeholders.length; i++) {
            if (i > 0) parts.push(',');
            parts.push('"');
            parts.push(JsonUtility.escapeJson(template.placeholders[i]));
            parts.push('"');
        }
    }
    parts.push(']');

    parts.push(',"components":[');
    if (template.components && template.components.length > 0) {
        for (let i = 0; i < template.components.length; i++) {
            if (i > 0) parts.push(',');
            parts.push(serializeTemplateInfoToJson(template.components[i]));
        }
    }
    parts.push(']');

    appendBool(parts, 'isStaticHtmlOnly', template.isStaticHtmlOnly);
    appendBool(parts, 'hasJsonKeys', template.hasJsonKeys);
    appendBool(parts, 'hasPlaceholders', template.hasPlaceholders);
    appendBool(parts, 'hasSlotholders', template.hasSlotholders);
    appendBool(parts, 'hasArrayBlocks', template.hasArrayBlocks);
    appendBool(parts, 'hasConditionals', template.hasConditionals);
    appendBool(parts, 'requiresDataJsonMerge', template.requiresDataJsonMerge);
    appendBool(parts, 'requiresContextMerge', template.requiresContextMerge);
    appendBool(parts, 'requiresTemplateTraversal', template.requiresTemplateTraversal);
    appendBool(parts, 'canReturnStaticHtml', template.canReturnStaticHtml);

    parts.push(',"entries":[');
    if (template.entries && template.entries.length > 0) {
        for (let i = 0; i < template.entries.length; i++) {
            if (i > 0) parts.push(',');
            appendTemplateEntryInfo(parts, template.entries[i]);
        }
    }
    parts.push(']');

    parts.push(',"jsonKeyEntries":[');
    if (template.jsonKeyEntries && template.jsonKeyEntries.length > 0) {
        for (let i = 0; i < template.jsonKeyEntries.length; i++) {
            if (i > 0) parts.push(',');
            appendJsonKeyEntryInfo(parts, template.jsonKeyEntries[i]);
        }
    }
    parts.push(']');

    parts.push('}');
    return parts.join('');
}

function appendBool(parts: string[], key: string, value: boolean): void {
    parts.push(',"');
    parts.push(key);
    parts.push('":');
    parts.push(value ? 'true' : 'false');
}

function appendTemplateEntryInfo(parts: string[], entry: TemplateEntryInfo): void {
    parts.push('{');
    parts.push('"htmlJsonKey":"');
    parts.push(JsonUtility.escapeJson(entry.htmlJsonKey || ''));
    parts.push('","entryType":"');
    parts.push(JsonUtility.escapeJson(entry.entryType || ''));
    parts.push('","name":"');
    parts.push(JsonUtility.escapeJson(entry.name || ''));
    parts.push('","ordinal":');
    parts.push(String(entry.ordinal || 0));
    parts.push(',"isSkipMarked":');
    parts.push(entry.isSkipMarked ? 'true' : 'false');
    parts.push(',"hasNestedContent":');
    parts.push(entry.hasNestedContent ? 'true' : 'false');
    parts.push('}');
}

function appendJsonKeyEntryInfo(parts: string[], entry: JsonKeyEntryInfo): void {
    parts.push('{');
    parts.push('"htmlJsonKey":"');
    parts.push(JsonUtility.escapeJson(entry.htmlJsonKey || ''));
    parts.push('","jsonKey":"');
    parts.push(JsonUtility.escapeJson(entry.jsonKey || ''));
    parts.push('","isSkipMarked":');
    parts.push(entry.isSkipMarked ? 'true' : 'false');
    parts.push(',"isInherited":');
    parts.push(entry.isInherited ? 'true' : 'false');
    parts.push('}');
}
